import { useState, useEffect } from "react";
import adminApi from "../api/adminAxios";

const STATUS_CONFIG = {
  pending:   { color: "#c47a3a", bg: "#fff4ec", label: "Pending"   },
  reviewing: { color: "#c4a020", bg: "#fffbec", label: "Reviewing" },
  approved:  { color: "#3a6ab0", bg: "#eef4ff", label: "Approved"  },
  rejected:  { color: "#c0392b", bg: "#fff0f0", label: "Rejected"  },
  completed: { color: "#3a8a68", bg: "#eefaf4", label: "Completed" },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const inr = (val) => val != null ? `₹${Number(val).toLocaleString("en-IN")}` : "—";

const fmt = (ts) => {
  const d = new Date(ts);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const CUST_STATUS_ICONS = {
  pending: "🕐", reviewing: "🔍", approved: "✅", rejected: "❌", completed: "🎉",
};

// Column widths — fixed so nothing ever truncates
const COL = "55px 150px 160px 120px 100px 55px 80px 50px 90px 110px 120px 90px";
const HEADERS = ["ID","NAME","EMAIL","SCENT","SIZE","QTY","COLOR","IMG","PRICE","PAYMENT","STATUS","ACTION"];

export default function ManageCustomizations() {
  const [requests, setRequests]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected]         = useState(null);
  const [updating, setUpdating]         = useState(null);
  const [accepting, setAccepting]       = useState(false);
  const [adminNote, setAdminNote]       = useState("");
  const [priceInput, setPriceInput]     = useState("");
  const [imageModal, setImageModal]     = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  useEffect(() => {
    adminApi.get("admin/customizations/")
      .then(r => setRequests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = requests.filter(r => {
    const matchSearch = !search ||
      [r.full_name, r.email, r.phone, String(r.id)]
        .some(v => v?.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdating(id);
    try {
      const res = await adminApi.patch(`admin/customizations/${id}/`, {
        status:     newStatus,
        admin_note: selected?.id === id ? adminNote : "",
      });
      setRequests(prev => prev.map(r =>
        r.id === id ? { ...r, status: res.data.status, admin_note: res.data.admin_note } : r
      ));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status: res.data.status }));
    } catch {}
    finally { setUpdating(null); }
  };

  const handleAccept = async () => {
    if (!selected) return;
    setAccepting(true); setAcceptSuccess(false);
    try {
      const res = await adminApi.patch(`admin/customizations/${selected.id}/`, {
        status: "approved", admin_note: adminNote,
        price: priceInput || null, accept: true,
      });
      setRequests(prev => prev.map(r =>
        r.id === selected.id
          ? { ...r, status: "approved", admin_note: adminNote, price: res.data.price } : r
      ));
      setSelected(prev => ({ ...prev, status: "approved", price: res.data.price }));
      setAcceptSuccess(true);
    } catch {}
    finally { setAccepting(false); }
  };

  const openHistory = async (req) => {
    setHistoryLoading(true);
    setHistoryModal({ req, entries: [] });
    try {
      const res = await adminApi.get(`admin/history/customization/${req.id}/`);
      setHistoryModal({ req, entries: res.data });
    } catch {
      setHistoryModal({ req, entries: [] });
    } finally { setHistoryLoading(false); }
  };

  const openDetail = (r) => {
    setSelected(r);
    setAdminNote(r.admin_note || "");
    setPriceInput(r.price || "");
    setAcceptSuccess(false);
  };

  const stats = {
    pending:   requests.filter(r => r.status === "pending").length,
    reviewing: requests.filter(r => r.status === "reviewing").length,
    approved:  requests.filter(r => r.status === "approved").length,
    completed: requests.filter(r => r.status === "completed").length,
  };

  return (
    <div style={{ padding: "32px 36px", fontFamily: "Georgia, serif" }}>

      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#1a1a1a" }}>Customization Requests</h1>
        <p style={{ fontSize: "13px", color: "#aaa", marginTop: "4px" }}>
          Review and manage custom candle requests from customers.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "14px", marginBottom: "24px" }}>
        {[
          { label: "Pending",   value: stats.pending,   color: "#c47a3a", bg: "#fff4ec", icon: "⏳" },
          { label: "Reviewing", value: stats.reviewing, color: "#c4a020", bg: "#fffbec", icon: "🔍" },
          { label: "Approved",  value: stats.approved,  color: "#3a6ab0", bg: "#eef4ff", icon: "✅" },
          { label: "Completed", value: stats.completed, color: "#3a8a68", bg: "#eefaf4", icon: "🎉" },
        ].map(({ label, value, color, bg, icon }) => (
          <div key={label} style={{ background: bg, borderRadius: "12px", padding: "16px 20px",
            display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "22px" }}>{icon}</span>
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color }}>{value}</p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#888" }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: "#fff", borderRadius: "14px", padding: "16px 20px",
        border: "1px solid #f0ebe3", marginBottom: "16px",
        display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "200px" }}>
          <span style={{ position: "absolute", left: "12px", top: "50%",
            transform: "translateY(-50%)", color: "#bbb" }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, ID..."
            style={{ width: "100%", padding: "8px 12px 8px 34px", borderRadius: "9px",
              border: "1.5px solid #e8d5b8", background: "#fffaf4",
              fontFamily: "Georgia, serif", fontSize: "12px",
              outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {["all", ...ALL_STATUSES].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{ padding: "6px 14px", borderRadius: "999px", border: "none",
                cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "11px",
                fontWeight: statusFilter === s ? "700" : "400",
                background: statusFilter === s ? "#e07b39" : "#f0ebe3",
                color: statusFilter === s ? "#fff" : "#888", textTransform: "capitalize" }}>
              {s === "all" ? "All" : STATUS_CONFIG[s]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table — horizontal scroll so ALL columns are visible ── */}
      <div style={{ background: "#fff", borderRadius: "14px",
        border: "1px solid #f0ebe3", overflow: "hidden" }}>

        {/* Scroll wrapper */}
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: "1400px" }}>

            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: COL,
              gap: "8px", padding: "12px 20px", background: "#f7f2eb",
              borderBottom: "1px solid #f0ebe3", minWidth: "1400px" }}>
              {HEADERS.map(h => (
                <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px",
                  fontWeight: "700", color: "#aaa", letterSpacing: "0.08em",
                  whiteSpace: "nowrap" }}>{h}</span>
              ))}
            </div>

            {loading ? (
              <p style={{ padding: "40px", color: "#aaa", fontFamily: "Georgia, serif",
                fontSize: "13px", textAlign: "center" }}>Loading...</p>
            ) : filtered.length === 0 ? (
              <p style={{ padding: "40px", color: "#aaa", fontFamily: "Georgia, serif",
                fontSize: "13px", textAlign: "center" }}>No customization requests found.</p>
            ) : (
              filtered.map(r => {
                const cfg = STATUS_CONFIG[r.status] || { color: "#888", bg: "#f5f5f5", label: r.status };
                return (
                  <div key={r.id}
                    style={{ display: "grid", gridTemplateColumns: COL,
                      gap: "8px", padding: "14px 20px",
                      borderBottom: "1px solid #f7f2eb", alignItems: "center",
                      transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fffaf4"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

                    {/* ID */}
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "12px",
                      fontWeight: "700", color: "#e07b39" }}>#{r.id}</span>

                    {/* NAME — full, never truncated */}
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                      fontWeight: "600", color: "#1a1a1a",
                      wordBreak: "break-word", lineHeight: "1.4" }}>
                      {r.full_name}
                    </span>

                    {/* EMAIL — separate column, wraps cleanly */}
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "11px",
                      color: "#666", wordBreak: "break-all", lineHeight: "1.4" }}>
                      {r.email}
                    </span>

                    <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555" }}>
                      {r.scent}
                    </span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555",
                      whiteSpace: "nowrap" }}>
                      {r.size}
                    </span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                      fontWeight: "700", color: "#1a1a1a", textAlign: "center" }}>
                      ×{r.quantity}
                    </span>
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555" }}>
                      {r.color || "—"}
                    </span>

                    {/* Ref image */}
                    <div>
                      {r.reference_image ? (
                        <img src={r.reference_image} alt="ref"
                          onClick={() => setImageModal(r.reference_image)}
                          title="Click to enlarge"
                          style={{ width: "36px", height: "36px", objectFit: "cover",
                            borderRadius: "6px", cursor: "zoom-in",
                            border: "1.5px solid #e8d5b8", display: "block" }} />
                      ) : (
                        <span style={{ color: "#ddd", fontSize: "11px" }}>—</span>
                      )}
                    </div>

                    {/* Price */}
                    <span style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                      fontWeight: "700", color: r.price ? "#3a8a68" : "#bbb",
                      whiteSpace: "nowrap" }}>
                      {r.price ? inr(r.price) : "—"}
                    </span>

                    {/* Payment */}
                    <span style={{ display: "inline-block", background: "#f0f9f0",
                      color: "#3a8a68", border: "1px solid #c8e6c9",
                      borderRadius: "999px", padding: "3px 10px",
                      fontFamily: "Georgia, serif", fontSize: "10px",
                      fontWeight: "700", whiteSpace: "nowrap" }}>
                      {r.payment_method || "COD"}
                    </span>

                    {/* Status */}
                    <span style={{ display: "inline-block", background: cfg.bg,
                      color: cfg.color, border: `1.5px solid ${cfg.color}`,
                      borderRadius: "999px", padding: "3px 10px",
                      fontFamily: "Georgia, serif", fontSize: "10px",
                      fontWeight: "700", whiteSpace: "nowrap" }}>
                      {cfg.label}
                    </span>

                    {/* Action */}
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => openDetail(r)}
                        style={{ padding: "5px 10px", borderRadius: "7px",
                          background: "#f0ebe3", border: "none", cursor: "pointer",
                          fontFamily: "Georgia, serif", fontSize: "11px",
                          fontWeight: "600", color: "#555", whiteSpace: "nowrap" }}>
                        View
                      </button>
                      <button onClick={() => openHistory(r)}
                        title="Status timeline"
                        style={{ padding: "5px 8px", borderRadius: "7px",
                          background: "#eef4ff", border: "1px solid #c8d8f0",
                          cursor: "pointer", fontSize: "12px" }}>
                        📄
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {!loading && (
          <div style={{ padding: "12px 20px", background: "#faf7f2" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>
              Showing {filtered.length} of {requests.length} requests
            </span>
          </div>
        )}
      </div>

      {/* ── Detail Modal ── */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
          zIndex: 1000, display: "flex", alignItems: "center",
          justifyContent: "center", padding: "20px" }}
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "28px",
            maxWidth: "620px", width: "100%",
            boxShadow: "0 20px 60px rgba(0,0,0,.18)",
            maxHeight: "92vh", overflowY: "auto" }}>

            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", marginBottom: "22px" }}>
              <div>
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: "17px",
                  fontWeight: "700", color: "#1a1a1a" }}>
                  Request #{selected.id}
                </h3>
                <span style={{ display: "inline-block", marginTop: "4px",
                  background: STATUS_CONFIG[selected.status]?.bg,
                  color: STATUS_CONFIG[selected.status]?.color,
                  border: `1px solid ${STATUS_CONFIG[selected.status]?.color}`,
                  borderRadius: "999px", padding: "2px 12px",
                  fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700" }}>
                  {STATUS_CONFIG[selected.status]?.label}
                </span>
              </div>
              <button onClick={() => setSelected(null)}
                style={{ background: "none", border: "none",
                  cursor: "pointer", fontSize: "20px", color: "#aaa" }}>✕</button>
            </div>

            {/* Customer Details */}
            <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#e07b39",
              textTransform: "uppercase", letterSpacing: "0.1em",
              fontWeight: "700", marginBottom: "10px" }}>👤 Customer Details</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "10px", marginBottom: "10px" }}>
              {[
                { label: "Full Name", value: selected.full_name },
                { label: "Email",     value: selected.email },
                { label: "Phone",     value: selected.phone },
                { label: "Payment",   value: selected.payment_method || "COD" },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: "#faf7f2", borderRadius: "10px", padding: "10px 14px" }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa",
                    textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>{label}</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                    fontWeight: "600", color: "#1a1a1a", wordBreak: "break-all" }}>{value}</p>
                </div>
              ))}
            </div>

            {selected.address && (
              <div style={{ background: "#faf7f2", borderRadius: "10px",
                padding: "10px 14px", marginBottom: "16px",
                border: "1px solid #e8d5b8" }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa",
                  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>📍 Delivery Address</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                  color: "#555", lineHeight: "1.5" }}>{selected.address}</p>
              </div>
            )}

            {/* Candle Details */}
            <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#e07b39",
              textTransform: "uppercase", letterSpacing: "0.1em",
              fontWeight: "700", marginBottom: "10px" }}>🕯️ Candle Details</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "10px", marginBottom: "16px" }}>
              {[
                { label: "Scent",    value: selected.scent },
                { label: "Size",     value: selected.size  },
                { label: "Color",    value: selected.color || "Not specified" },
                { label: "Quantity", value: `×${selected.quantity}` },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: "#faf7f2", borderRadius: "10px", padding: "10px 14px" }}>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa",
                    textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>{label}</p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                    fontWeight: "600", color: "#1a1a1a" }}>{value}</p>
                </div>
              ))}
            </div>

            {selected.label_text && (
              <div style={{ background: "#faf7f2", borderRadius: "10px",
                padding: "10px 14px", marginBottom: "10px" }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa",
                  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>Label Text</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "13px",
                  color: "#555", fontStyle: "italic" }}>"{selected.label_text}"</p>
              </div>
            )}
            {selected.notes && (
              <div style={{ background: "#faf7f2", borderRadius: "10px",
                padding: "10px 14px", marginBottom: "16px" }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa",
                  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "3px" }}>Notes</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#555" }}>{selected.notes}</p>
              </div>
            )}

            {selected.reference_image && (
              <div style={{ background: "#faf7f2", borderRadius: "10px",
                padding: "12px 14px", marginBottom: "16px" }}>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa",
                  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "8px" }}>Reference Image</p>
                <img src={selected.reference_image} alt="Reference"
                  onClick={() => setImageModal(selected.reference_image)}
                  style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain",
                    borderRadius: "8px", cursor: "zoom-in", border: "1px solid #e8d5b8",
                    display: "block" }} />
                <p style={{ fontFamily: "Georgia, serif", fontSize: "10px",
                  color: "#bbb", marginTop: "5px" }}>Click to enlarge</p>
              </div>
            )}

            {/* Status Update */}
            <div style={{ background: "#faf7f2", borderRadius: "12px",
              padding: "16px", marginBottom: "16px" }}>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa",
                textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "10px" }}>
                Update Status
              </p>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {ALL_STATUSES.map(s => {
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <button key={s}
                      onClick={() => handleStatusUpdate(selected.id, s)}
                      disabled={updating === selected.id}
                      style={{ padding: "6px 14px", borderRadius: "999px",
                        border: `1.5px solid ${selected.status === s ? cfg.color : "#e8d5b8"}`,
                        background: selected.status === s ? cfg.bg : "#fff",
                        color: selected.status === s ? cfg.color : "#888",
                        cursor: "pointer", fontFamily: "Georgia, serif",
                        fontSize: "11px", fontWeight: selected.status === s ? "700" : "400" }}>
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px",
                color: "#bbb", marginTop: "8px" }}>
                Changing status automatically sends an email to the customer.
              </p>
            </div>

            {/* Accept Customization */}
            <div style={{ background: "#fff8f0", borderRadius: "12px",
              padding: "18px", border: "1.5px solid #f0d5b0" }}>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#c47a3a",
                textTransform: "uppercase", letterSpacing: "0.1em",
                fontWeight: "700", marginBottom: "10px" }}>✅ Accept Customization</p>

              {acceptSuccess && (
                <div style={{ background: "#eefaf4", border: "1px solid #3a8a68",
                  borderRadius: "8px", padding: "10px 14px", marginBottom: "12px",
                  fontFamily: "Georgia, serif", fontSize: "12px", color: "#3a8a68" }}>
                  ✅ Accepted! Confirmation email sent to {selected.email}.
                </div>
              )}

              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px",
                color: "#7a5c38", marginBottom: "14px", lineHeight: "1.6" }}>
                Set the price and add a note. The customer receives a detailed acceptance email with all candle details and COD info.
              </p>

              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa",
                textTransform: "uppercase", letterSpacing: "0.1em",
                display: "block", marginBottom: "6px" }}>Price (₹)</p>
              <input type="number" value={priceInput}
                onChange={e => setPriceInput(e.target.value)}
                placeholder="e.g. 1200" min="0"
                style={{ width: "100%", padding: "10px 12px", borderRadius: "9px",
                  border: "1.5px solid #e8d5b8", background: "#fffaf4",
                  fontFamily: "Georgia, serif", fontSize: "13px", outline: "none",
                  boxSizing: "border-box", marginBottom: "12px" }} />

              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa",
                textTransform: "uppercase", letterSpacing: "0.1em",
                display: "block", marginBottom: "6px" }}>Note to Customer (optional)</p>
              <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)}
                rows={2}
                placeholder="e.g. Your candle will be crafted within 5–7 working days..."
                style={{ width: "100%", padding: "9px 12px", borderRadius: "9px",
                  border: "1.5px solid #e8d5b8", background: "#fffaf4",
                  fontFamily: "Georgia, serif", fontSize: "12px", outline: "none",
                  boxSizing: "border-box", resize: "none", marginBottom: "14px" }} />

              <button onClick={handleAccept} disabled={accepting}
                style={{ width: "100%", padding: "13px", borderRadius: "10px",
                  background: accepting ? "#ccc" : "linear-gradient(135deg,#e07b39,#c96a18)",
                  color: "#fff", border: "none",
                  cursor: accepting ? "not-allowed" : "pointer",
                  fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700" }}>
                {accepting ? "Sending..." : "✅ Accept Customization & Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
          zIndex: 1100, display: "flex", alignItems: "center",
          justifyContent: "center", padding: "24px" }}
          onClick={e => { if (e.target === e.currentTarget) setHistoryModal(null); }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "32px",
            maxWidth: "540px", width: "100%",
            boxShadow: "0 20px 60px rgba(0,0,0,.2)", maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "flex-start", marginBottom: "24px" }}>
              <div>
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: "17px",
                  fontWeight: "700", color: "#1a1a1a" }}>
                  Customization #{historyModal.req.id} — Timeline
                </h3>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "12px",
                  color: "#aaa", marginTop: "4px" }}>{historyModal.req.full_name}</p>
              </div>
              <button onClick={() => setHistoryModal(null)}
                style={{ background: "none", border: "none",
                  cursor: "pointer", fontSize: "20px", color: "#aaa" }}>✕</button>
            </div>

            {historyLoading ? (
              <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "13px",
                textAlign: "center", padding: "32px 0" }}>Loading timeline...</p>
            ) : historyModal.entries.length === 0 ? (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa",
                textAlign: "center", padding: "32px 0" }}>
                No history yet. History is recorded from this point forward.
              </p>
            ) : (
              <div style={{ position: "relative", paddingLeft: "28px",
                borderLeft: "2px solid #f0ebe3" }}>
                {historyModal.entries.map((entry, i) => {
                  const cfg = STATUS_CONFIG[entry.status] || { color: "#888", bg: "#faf7f2" };
                  const icon = CUST_STATUS_ICONS[entry.status] || "•";
                  const isLast = i === historyModal.entries.length - 1;
                  return (
                    <div key={entry.id}
                      style={{ position: "relative", marginBottom: isLast ? 0 : "20px" }}>
                      <div style={{ position: "absolute", left: "-37px", top: "3px",
                        width: "16px", height: "16px", borderRadius: "50%",
                        background: isLast ? cfg.color : "#fff",
                        border: `2px solid ${cfg.color}`,
                        display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {isLast && <span style={{ color: "#fff", fontSize: "9px" }}>✓</span>}
                      </div>
                      <div style={{ background: isLast ? cfg.bg : "#faf7f2",
                        border: `1px solid ${isLast ? cfg.color : "#f0ebe3"}`,
                        borderRadius: "10px", padding: "12px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: entry.note ? "6px" : 0 }}>
                          <span style={{ fontFamily: "Georgia, serif", fontSize: "14px",
                            fontWeight: "700", color: cfg.color, textTransform: "capitalize" }}>
                            {icon} {entry.status}
                          </span>
                          <span style={{ fontFamily: "Georgia, serif",
                            fontSize: "11px", color: "#aaa" }}>
                            {fmt(entry.timestamp)}
                          </span>
                        </div>
                        {entry.note && (
                          <p style={{ fontFamily: "Georgia, serif", fontSize: "12px",
                            color: "#888", fontStyle: "italic", marginTop: "4px" }}>
                            Note: {entry.note}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {imageModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.88)",
          zIndex: 2000, display: "flex", alignItems: "center",
          justifyContent: "center", padding: "24px", cursor: "zoom-out" }}
          onClick={() => setImageModal(null)}>
          <img src={imageModal} alt="Reference"
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain",
              borderRadius: "12px", boxShadow: "0 8px 40px rgba(0,0,0,.5)" }} />
          <button onClick={() => setImageModal(null)}
            style={{ position: "absolute", top: "20px", right: "24px",
              background: "rgba(255,255,255,.15)", border: "none", borderRadius: "50%",
              width: "38px", height: "38px", cursor: "pointer",
              color: "#fff", fontSize: "18px" }}>✕</button>
        </div>
      )}
    </div>
  );
}